import { useCallback } from 'react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { getUser } from '@frontend/auth-helpers';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { useSmsTagNotification } from '@frontend/notifications';
import { useAppScopeStore } from '@frontend/scope';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

/**
 * A hook that returns a function to execute when a manual scheduled SMS event is received.
 * This doesn't have any WebSocket subscription logic, just the handler. Useful for testing.
 */
export const useSMSTagNotificationWSHandler = () => {
  const { navigateToThread } = useInboxNavigate();
  const userId = getUser()?.userID;
  const { selectedLocationIds } = useAppScopeStore();
  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);
  const { create } = useSmsTagNotification({
    onView: (event) => {
      navigateToThread({
        threadId: event.payload.threadId,
        groupId: event.payload.groupId,
        personId: event.payload.personId,
        threadItemId: event.payload.smsId,
        threadItemCreatedAt: event.payload.smsCreatedAt,
      });
    },
  });

  const eventHandler = useCallback<GetWebsocketEventHandler<NotificationType.SMS_TAG_V2>>(
    (event) => {
      const {
        params: { action, userId: taggedByUserId, weaveNumber },
      } = event;

      if (taggedByUserId === userId) {
        // Ignore events triggered by the current user
        return;
      }

      // If User does not have access to location phone number in the event's thread, ignore the event
      if (!!weaveNumber && !checkUserLocationPhoneAccess(weaveNumber)) {
        return;
      }

      switch (action) {
        case 'tagApplied':
          create({
            id: String(event.id),
            type: 'sms-tag',
            payload: event.params,
            timestamp: Date.now(),
            state: {
              paused: false,
              status: 'unread',
            },
          });
      }
    },
    [create]
  );

  return eventHandler;
};

export const useSMSTagNotificationSubscription = () => {
  const handler = useSMSTagNotificationWSHandler();
  return useWebsocketEventSubscription(NotificationType.SMS_TAG_V2, handler);
};
